import firebase, {
  functionBaseUrl,
  axiosWithToken,
} from "../../../common/firebase";
import { isTestAccount } from "./summaryUtils";
import { CAMERA_ANGLE_ALIASES } from "../../../common/videos";

const db = firebase.firestore();

/**
 * Helper function to fetch all documents from a collection in batches
 * with retry logic to handle timeouts
 */
async function fetchCollectionInBatches(
  collectionName,
  batchSize = 500,
  maxRetries = 3
) {
  const allDocs = [];
  let lastDoc = null;
  let retryCount = 0;
  let batchNumber = 0;

  while (true) {
    try {
      let query = db.collection(collectionName).limit(batchSize);

      if (lastDoc) {
        query = query.startAfter(lastDoc);
      }

      const snapshot = await query.get();

      if (snapshot.empty) {
        break;
      }

      snapshot.forEach((doc) => {
        allDocs.push({ id: doc.id, data: doc.data() });
      });

      batchNumber++;
      console.log(
        `Fetched batch ${batchNumber} of ${collectionName}: ${snapshot.size} documents (total: ${allDocs.length})`
      );

      // If we got fewer documents than the batch size, we've reached the end
      if (snapshot.size < batchSize) {
        break;
      }

      // Get the last document for pagination
      lastDoc = snapshot.docs[snapshot.docs.length - 1];
      retryCount = 0; // Reset retry count on success
    } catch (error) {
      retryCount++;
      if (retryCount > maxRetries) {
        console.error(
          `Error fetching ${collectionName} after ${maxRetries} retries:`,
          error
        );
        throw error;
      }
      // Wait before retrying (exponential backoff)
      const delay = Math.min(1000 * Math.pow(2, retryCount - 1), 10000);
      console.warn(
        `Retrying ${collectionName} fetch (attempt ${retryCount}/${maxRetries}) after ${delay}ms`
      );
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }

  console.log(
    `Completed fetching ${collectionName}: ${allDocs.length} total documents`
  );
  return allDocs;
}

export async function fetchAnalysisData(
  dateRange,
  index,
  analysisData,
  analysisDataFON,
  analysisDataDTL
) {
  try {
    // Query for Face-On (FON) analysis or where cameraAngle is empty or does not exist
    // Note: Compat SDK doesn't support OR directly, so we query separately and combine
    const qFON1 = db
      .collection("analysis")
      .where("createdDate", ">=", String(dateRange.start))
      .where("createdDate", "<=", String(dateRange.end))
      .where("videoMetadata.cameraAngle", "in", CAMERA_ANGLE_ALIASES.FON);

    const qFON2 = db
      .collection("analysis")
      .where("createdDate", ">=", String(dateRange.start))
      .where("createdDate", "<=", String(dateRange.end))
      .where("videoMetadata.cameraAngle", "==", null);

    const qFON3 = db
      .collection("analysis")
      .where("createdDate", ">=", String(dateRange.start))
      .where("createdDate", "<=", String(dateRange.end))
      .where("videoMetadata.cameraAngle", "==", "");

    // Query for Down-The-Line (DTL) analysis
    const qDTL = db
      .collection("analysis")
      .where("createdDate", ">=", String(dateRange.start))
      .where("createdDate", "<=", String(dateRange.end))
      .where("videoMetadata.cameraAngle", "in", CAMERA_ANGLE_ALIASES.DTL);

    // Execute queries and get counts
    const [snapshotFON1, snapshotFON2, snapshotFON3, snapshotDTL] =
      await Promise.all([qFON1.get(), qFON2.get(), qFON3.get(), qDTL.get()]);

    // Combine FON results (using Set to avoid duplicates)
    const fonIds = new Set();
    snapshotFON1.forEach((doc) => fonIds.add(doc.id));
    snapshotFON2.forEach((doc) => fonIds.add(doc.id));
    snapshotFON3.forEach((doc) => fonIds.add(doc.id));

    // Update the analysis data arrays
    analysisDataFON[index] = fonIds.size;
    analysisDataDTL[index] = snapshotDTL.size;
    analysisData[index] = analysisDataFON[index] + analysisDataDTL[index];
  } catch (error) {
    console.error("Error fetching analysis data:", error);
    analysisDataFON[index] = 0;
    analysisDataDTL[index] = 0;
    analysisData[index] = 0;
  }
}

export async function fetchIssuesData(
  dateRange,
  index,
  issuesData,
  issuesDataFON,
  issuesDataDTL
) {
  try {
    // Query for Face-On (FON) issues or where cameraAngle is empty or does not exist
    // Note: Compat SDK doesn't support OR directly, so we query separately and combine
    const qFON1 = db
      .collection("reportIssues")
      .where("createdDate", ">=", String(dateRange.start))
      .where("createdDate", "<=", String(dateRange.end))
      .where("cameraAngle", "in", CAMERA_ANGLE_ALIASES.FON);

    const qFON2 = db
      .collection("reportIssues")
      .where("createdDate", ">=", String(dateRange.start))
      .where("createdDate", "<=", String(dateRange.end))
      .where("cameraAngle", "==", null);

    const qFON3 = db
      .collection("reportIssues")
      .where("createdDate", ">=", String(dateRange.start))
      .where("createdDate", "<=", String(dateRange.end))
      .where("cameraAngle", "==", "");

    // Query for Down-The-Line (DTL) issues
    const qDTL = db
      .collection("reportIssues")
      .where("createdDate", ">=", String(dateRange.start))
      .where("createdDate", "<=", String(dateRange.end))
      .where("cameraAngle", "in", CAMERA_ANGLE_ALIASES.DTL);

    // Execute queries and get counts
    const [snapshotFON1, snapshotFON2, snapshotFON3, snapshotDTL] =
      await Promise.all([qFON1.get(), qFON2.get(), qFON3.get(), qDTL.get()]);

    // Combine FON results (using Set to avoid duplicates)
    const fonIds = new Set();
    snapshotFON1.forEach((doc) => fonIds.add(doc.id));
    snapshotFON2.forEach((doc) => fonIds.add(doc.id));
    snapshotFON3.forEach((doc) => fonIds.add(doc.id));

    // Update the issues data arrays
    issuesDataFON[index] = fonIds.size;
    issuesDataDTL[index] = snapshotDTL.size;
    issuesData[index] = issuesDataFON[index] + issuesDataDTL[index];
  } catch (error) {
    console.error("Error fetching issues data:", error);
    issuesDataFON[index] = 0;
    issuesDataDTL[index] = 0;
    issuesData[index] = 0;
  }
}

export async function fetchAnalysisPctData(
  dateRange,
  index,
  analysisData,
  analysisDataFON,
  analysisDataDTL
) {
  try {
    // Query for Face-On (FON) analysis or where cameraAngle is empty or does not exist
    // Note: Compat SDK doesn't support OR directly, so we query separately and combine
    const qFON1 = db
      .collection("analysis")
      .where("createdDate", ">=", String(dateRange.start))
      .where("createdDate", "<=", String(dateRange.end))
      .where("videoMetadata.cameraAngle", "in", CAMERA_ANGLE_ALIASES.FON);

    const qFON2 = db
      .collection("analysis")
      .where("createdDate", ">=", String(dateRange.start))
      .where("createdDate", "<=", String(dateRange.end))
      .where("videoMetadata.cameraAngle", "==", null);

    const qFON3 = db
      .collection("analysis")
      .where("createdDate", ">=", String(dateRange.start))
      .where("createdDate", "<=", String(dateRange.end))
      .where("videoMetadata.cameraAngle", "==", "");

    // Query for Down-The-Line (DTL) analysis
    const qDTL = db
      .collection("analysis")
      .where("createdDate", ">=", String(dateRange.start))
      .where("createdDate", "<=", String(dateRange.end))
      .where("videoMetadata.cameraAngle", "in", CAMERA_ANGLE_ALIASES.DTL);

    // Execute queries and get counts
    const [snapshotFON1, snapshotFON2, snapshotFON3, snapshotDTL] =
      await Promise.all([qFON1.get(), qFON2.get(), qFON3.get(), qDTL.get()]);

    // Combine FON results (using Set to avoid duplicates)
    const fonIds = new Set();
    snapshotFON1.forEach((doc) => fonIds.add(doc.id));
    snapshotFON2.forEach((doc) => fonIds.add(doc.id));
    snapshotFON3.forEach((doc) => fonIds.add(doc.id));

    const fonCount = fonIds.size;
    const dtlCount = snapshotDTL.size;

    // Calculate percentages
    const total = fonCount + dtlCount;
    analysisData[index] = total;
    analysisDataFON[index] =
      total > 0 ? Math.round((fonCount / total) * 100) : 0;
    analysisDataDTL[index] =
      total > 0 ? Math.round((dtlCount / total) * 100) : 0;
  } catch (error) {
    console.error("Error fetching analysis percentage data:", error);
    analysisData[index] = 0;
    analysisDataFON[index] = 0;
    analysisDataDTL[index] = 0;
  }
}

export async function fetchIssuesPctData(
  dateRange,
  index,
  issuesData,
  issuesDataFON,
  issuesDataDTL
) {
  try {
    // Query for Face-On (FON) issues or where cameraAngle is empty or does not exist
    // Note: Compat SDK doesn't support OR directly, so we query separately and combine
    const qFON1 = db
      .collection("reportIssues")
      .where("createdDate", ">=", String(dateRange.start))
      .where("createdDate", "<=", String(dateRange.end))
      .where("cameraAngle", "in", CAMERA_ANGLE_ALIASES.FON);

    const qFON2 = db
      .collection("reportIssues")
      .where("createdDate", ">=", String(dateRange.start))
      .where("createdDate", "<=", String(dateRange.end))
      .where("cameraAngle", "==", null);

    const qFON3 = db
      .collection("reportIssues")
      .where("createdDate", ">=", String(dateRange.start))
      .where("createdDate", "<=", String(dateRange.end))
      .where("cameraAngle", "==", "");

    // Query for Down-The-Line (DTL) issues
    const qDTL = db
      .collection("reportIssues")
      .where("createdDate", ">=", String(dateRange.start))
      .where("createdDate", "<=", String(dateRange.end))
      .where("cameraAngle", "in", CAMERA_ANGLE_ALIASES.DTL);

    // Execute queries and get counts
    const [snapshotFON1, snapshotFON2, snapshotFON3, snapshotDTL] =
      await Promise.all([qFON1.get(), qFON2.get(), qFON3.get(), qDTL.get()]);

    // Combine FON results (using Set to avoid duplicates)
    const fonIds = new Set();
    snapshotFON1.forEach((doc) => fonIds.add(doc.id));
    snapshotFON2.forEach((doc) => fonIds.add(doc.id));
    snapshotFON3.forEach((doc) => fonIds.add(doc.id));

    const fonCount = fonIds.size;
    const dtlCount = snapshotDTL.size;

    // Calculate percentages
    const total = fonCount + dtlCount;
    issuesData[index] = total;
    issuesDataFON[index] = total > 0 ? Math.round((fonCount / total) * 100) : 0;
    issuesDataDTL[index] = total > 0 ? Math.round((dtlCount / total) * 100) : 0;
  } catch (error) {
    console.error("Error fetching issues percentage data:", error);
    issuesData[index] = 0;
    issuesDataFON[index] = 0;
    issuesDataDTL[index] = 0;
  }
}

export async function fetchUserData(
  dateRange,
  index,
  dataArr,
  userType,
  subscriptionTypes = []
) {
  try {
    let q;

    // Base query for all users
    if (userType === "users") {
      q = db
        .collection("users")
        .where("userCreated", ">=", dateRange.start)
        .where("userCreated", "<=", dateRange.end);
    }

    // Query for coaches with specific subscription types
    if (userType === "coaches") {
      q = db
        .collection("users")
        .where("userCreated", ">=", dateRange.start)
        .where("userCreated", "<=", dateRange.end)
        .where("subscriptionType", "in", subscriptionTypes);
    }

    // Query for students with specific subscription types
    if (userType === "students") {
      q = db
        .collection("users")
        .where("userCreated", ">=", dateRange.start)
        .where("userCreated", "<=", dateRange.end)
        .where("subscriptionType", "in", subscriptionTypes);
    }

    // Get the count of documents
    const snapshot = await q.get();
    dataArr[index] = snapshot.size;
  } catch (error) {
    console.error(`Error fetching ${userType} data:`, error);
    dataArr[index] = 0; // Set to 0 in case of an error
  }
}

export async function fetchSessionData(dateRange, index, sessionsData) {
  try {
    // Query Firestore for sessions within the date range
    const q = db
      .collection("sessions")
      .where("sessionDate", ">=", String(dateRange.start))
      .where("sessionDate", "<=", String(dateRange.end));

    // Get the count of documents
    const snapshot = await q.get();
    sessionsData[index] = snapshot.size;
  } catch (error) {
    console.error("Error fetching session data:", error);
    sessionsData[index] = 0; // Set to 0 in case of an error
  }
}

export async function fetchVideoData(
  dateRange,
  index,
  videosData,
  videosDataFON,
  videosDataDTL
) {
  try {
    // Define queries for all videos, Face-On (FON), and Down-The-Line (DTL)
    const qVideos = db
      .collection("videos")
      .where("videoCreated", ">=", dateRange.start)
      .where("videoCreated", "<=", dateRange.end);

    // Note: Compat SDK doesn't support OR directly, so we query separately and combine
    const qFON1 = db
      .collection("videos")
      .where("videoCreated", ">=", dateRange.start)
      .where("videoCreated", "<=", dateRange.end)
      .where("metaData.cameraAngle", "in", CAMERA_ANGLE_ALIASES.FON);

    const qFON2 = db
      .collection("videos")
      .where("videoCreated", ">=", dateRange.start)
      .where("videoCreated", "<=", dateRange.end)
      .where("metaData.cameraAngle", "==", null);

    const qFON3 = db
      .collection("videos")
      .where("videoCreated", ">=", dateRange.start)
      .where("videoCreated", "<=", dateRange.end)
      .where("metaData.cameraAngle", "==", "");

    const qDTL = db
      .collection("videos")
      .where("videoCreated", ">=", dateRange.start)
      .where("videoCreated", "<=", dateRange.end)
      .where("metaData.cameraAngle", "in", CAMERA_ANGLE_ALIASES.DTL);

    // Execute all queries concurrently using Promise.all
    const [
      snapshotVideos,
      snapshotFON1,
      snapshotFON2,
      snapshotFON3,
      snapshotDTL,
    ] = await Promise.all([
      qVideos.get(),
      qFON1.get(),
      qFON2.get(),
      qFON3.get(),
      qDTL.get(),
    ]);

    // Combine FON results (using Set to avoid duplicates)
    const fonIds = new Set();
    snapshotFON1.forEach((doc) => fonIds.add(doc.id));
    snapshotFON2.forEach((doc) => fonIds.add(doc.id));
    snapshotFON3.forEach((doc) => fonIds.add(doc.id));

    // Update the data arrays with the results
    videosData[index] = snapshotVideos.size;
    videosDataFON[index] = fonIds.size;
    videosDataDTL[index] = snapshotDTL.size;
  } catch (error) {
    console.error("Error fetching video data:", error);
    videosData[index] = 0;
    videosDataFON[index] = 0;
    videosDataDTL[index] = 0;
  }
}

/**
 * Fetches device limit exception list from mobile app settings
 */
export async function fetchDeviceLimitExceptionList() {
  try {
    const deviceSnap = await firebase
      .firestore()
      .collection("mobileAppSettings")
      .doc("X3X42cbYDIT9t9BL43bz")
      .get();
    return deviceSnap?.data()?.deviceLimitExceptionList || [];
  } catch (error) {
    console.error("Error fetching device limit exception list:", error);
    return [];
  }
}

/**
 * Fetches all subscriptions and processes them into categorized arrays
 * Uses pagination to handle large collections and prevent timeouts
 */
export async function fetchAllSubscriptions(deviceLimitExceptionList = []) {
  try {
    // Fetch all subscriptions in batches to prevent timeouts
    const subsDocs = await fetchCollectionInBatches("subscriptions", 500, 3);

    if (!subsDocs || subsDocs.length === 0) {
      console.log(
        "No subscriptions found or error getting Subscriptions documents"
      );
      return {
        allSubs: [],
        elavonSubs: [],
        stripeSubs: [],
        appleSubs: [],
        androidSubs: [],
        elavonSubs2: [],
        stripeSubs2: [],
        appleSubs2: [],
        androidSubs2: [],
        elavonCanceledSubs: [],
        stripeCanceledSubs: [],
        appleCanceledSubs: [],
        androidCanceledSubs: [],
      };
    }

    const allSubs = [];
    const elavonSubs = [];
    const stripeSubs = [];
    const appleSubs = [];
    const androidSubs = [];
    const elavonSubs2 = [];
    const stripeSubs2 = [];
    const appleSubs2 = [];
    const androidSubs2 = [];
    const elavonCanceledSubs = [];
    const stripeCanceledSubs = [];
    const appleCanceledSubs = [];
    const androidCanceledSubs = [];

    subsDocs.forEach(({ id, data }) => {
      let subscriptionData = data;
      if (
        !isTestAccount(subscriptionData?.userEmail, deviceLimitExceptionList)
      ) {
        if (subscriptionData.platform === "Elavon") {
          if (subscriptionData.autoRenewal) {
            elavonSubs.push({ ...subscriptionData, id });
          } else {
            if (
              parseInt(subscriptionData.amount) === 0 ||
              (subscriptionData.transactionId !== "" &&
                (parseInt(subscriptionData.amount) === 100 ||
                  parseInt(subscriptionData.amount) === 200))
            ) {
              elavonSubs.push({ ...subscriptionData, id });
            }
            if (subscriptionData.transactionId !== "") {
              elavonCanceledSubs.push({ ...subscriptionData, id });
            }
          }
          if (
            subscriptionData.endDate > String(Date.now()) &&
            subscriptionData.transactionId !== "" &&
            !subscriptionData?.bootcampUser
          ) {
            elavonSubs2.push({ ...subscriptionData, id });
          }
        }
        if (subscriptionData.platform === "Apple") {
          if (subscriptionData.autoRenewal) {
            appleSubs.push({ ...subscriptionData, id });
          } else {
            appleCanceledSubs.push({ ...subscriptionData, id });
          }
          if (subscriptionData.endDate > String(Date.now())) {
            appleSubs2.push({ ...subscriptionData, id });
          }
        }
        if (subscriptionData.platform === "Android") {
          if (subscriptionData.autoRenewal) {
            androidSubs.push({ ...subscriptionData, id });
          } else {
            androidCanceledSubs.push({ ...subscriptionData, id });
          }
          if (subscriptionData.endDate > String(Date.now())) {
            androidSubs2.push({ ...subscriptionData, id });
          }
        }
        if (subscriptionData.platform === "Stripe") {
          if (subscriptionData.autoRenewal) {
            stripeSubs.push({ ...subscriptionData, id });
          } else {
            if (
              parseInt(subscriptionData.amount) === 0 ||
              (subscriptionData.transactionId !== "" &&
                (parseInt(subscriptionData.amount) === 100 ||
                  parseInt(subscriptionData.amount) === 200))
            ) {
              stripeSubs.push({ ...subscriptionData, id });
            }
            if (subscriptionData.transactionId !== "") {
              stripeCanceledSubs.push({ ...subscriptionData, id });
            }
          }
          if (
            subscriptionData.endDate > String(Date.now()) &&
            subscriptionData.transactionId !== "" &&
            !subscriptionData?.bootcampUser
          ) {
            stripeSubs2.push({ ...subscriptionData, id });
          }
        }
        allSubs.push({ ...subscriptionData, id });
      }
    });

    const result = {
      allSubs,
      elavonSubs,
      stripeSubs,
      appleSubs,
      androidSubs,
      elavonSubs2,
      stripeSubs2,
      appleSubs2,
      androidSubs2,
      elavonCanceledSubs,
      stripeCanceledSubs,
      appleCanceledSubs,
      androidCanceledSubs,
    };
    return result;
  } catch (error) {
    console.error("Error fetching subscriptions:", error);
    return {
      allSubs: [],
      elavonSubs: [],
      stripeSubs: [],
      appleSubs: [],
      androidSubs: [],
      elavonSubs2: [],
      stripeSubs2: [],
      appleSubs2: [],
      androidSubs2: [],
      elavonCanceledSubs: [],
      stripeCanceledSubs: [],
      appleCanceledSubs: [],
      androidCanceledSubs: [],
    };
  }
}

/**
 * Fetches all invites from Firestore
 */
export async function fetchAllInvites(deviceLimitExceptionList = []) {
  try {
    const inviteSnap = await firebase.firestore().collection("invites").get();

    if (!inviteSnap) {
      console.log("Error getting Invites documents");
      return {
        allInvites: [],
        allCoachesInvites: [],
        allStudentsInvites: [],
      };
    }

    const allInvites = [];
    const allCoachesInvites = [];
    const allStudentsInvites = [];

    inviteSnap.forEach((doc) => {
      let data = doc.data();
      if (!isTestAccount(data?.invitee, deviceLimitExceptionList)) {
        allInvites.push({ ...doc.data(), id: doc.id });
        if (data.role === "Sportsbox-Instructor") {
          allCoachesInvites.push({ ...doc.data(), id: doc.id });
        }
        if (data.role === "Sportsbox-User") {
          allStudentsInvites.push({ ...doc.data(), id: doc.id });
        }
      }
    });

    // Update count document
    await firebase
      .firestore()
      .collection("count")
      .doc("invites")
      .update({ total: inviteSnap.size, lastUpdated: String(Date.now()) });

    const result = {
      allInvites,
      allCoachesInvites,
      allStudentsInvites,
    };
    return result;
  } catch (error) {
    console.error("Error fetching invites:", error);
    return {
      allInvites: [],
      allCoachesInvites: [],
      allStudentsInvites: [],
    };
  }
}

/**
 * Fetches NY Campaign users (users with shotTrajectory)
 */
export async function fetchNYCampaignUsers(deviceLimitExceptionList = []) {
  try {
    const nySnap = await firebase
      .firestore()
      .collection("users")
      .where("shotTrajectory", "!=", "")
      .get();

    const nyUsers = [];
    nySnap.forEach((doc) => {
      if (!isTestAccount(doc.data()?.email, deviceLimitExceptionList)) {
        nyUsers.push({ ...doc.data(), id: doc.id });
      }
    });

    return nyUsers;
  } catch (error) {
    console.error("Error fetching NY Campaign users:", error);
    return [];
  }
}

/**
 * Fetches count collection data and processes it
 */
export async function fetchCollectionCounts() {
  try {
    const countSnap = await firebase.firestore().collection("count").get();

    if (!countSnap) {
      console.log("Error getting Count documents");
      return [];
    }

    const collCounts = [];
    countSnap.forEach((doc) => {
      let data = doc.data();
      if (doc.id === "users") {
        collCounts.push({
          id: "All Coaches",
          total: data.coaches,
          lastUpdated: data.lastUpdated,
        });
        collCounts.push({
          id: "Free Students",
          total: data.students,
          lastUpdated: data.lastUpdated,
        });
        collCounts.push({
          id: "Paid Students",
          total: data.paidStudents,
          lastUpdated: data.lastUpdated,
        });
        collCounts.push({
          id: "Free Students/Coach",
          total: data.freeStudentsPerProCoach,
          lastUpdated: data.lastUpdated,
        });
        collCounts.push({
          id: "Paid Students/Coach",
          total: data.paidStudentsPerProCoach,
          lastUpdated: data.lastUpdated,
        });
        collCounts.push({
          id: "Free Students/Staff Coach",
          total: data.freeStudentsPerStaffCoach,
          lastUpdated: data.lastUpdated,
        });
        collCounts.push({
          id: "Paid Students/Staff Coach",
          total: data.paidStudentsPerStaffCoach,
          lastUpdated: data.lastUpdated,
        });
      }
      if (doc.id === "sessions") {
        collCounts.push({
          id: "Sessions/User",
          total: Number(data.sessionsAvg).toFixed(2),
          lastUpdated: data.lastUpdated,
        });
      }
      if (doc.id === "videos") {
        collCounts.push({
          id: "Videos/Session",
          total: Number(data.videosAvg).toFixed(2),
          lastUpdated: data.lastUpdated,
        });
      }
      if (doc.id === "invites") {
        collCounts.push({
          id: "Invites",
          total: Number(data.total),
          lastUpdated: data.lastUpdated,
        });
        collCounts.push({
          id: "Approved Invites",
          total: Number(data.approved),
          lastUpdated: data.lastUpdated,
        });
        collCounts.push({
          id: "Pending Invites",
          total: Number(data.pending),
          lastUpdated: data.lastUpdated,
        });
        collCounts.push({
          id: "Approved/Pending Invites",
          total: Number(data.invitesAvg).toFixed(2),
          lastUpdated: data.lastUpdated,
        });
      }
      if (doc.id === "subscriptions") {
        collCounts.push({
          id: "Subscriptions",
          total: data.total,
          lastUpdated: data.lastUpdated,
        });
        collCounts.push({
          id: "Active Coaches Subscriptions",
          total: data?.activeCoaches,
          lastUpdated: data.lastUpdated,
        });
        collCounts.push({
          id: "Active Students Subscriptions",
          total: data?.activeStudents,
          lastUpdated: data.lastUpdated,
        });
      }
    });

    return collCounts;
  } catch (error) {
    console.error("Error fetching collection counts:", error);
    return [];
  }
}

/**
 * Fetches free trials from API
 */
export async function fetchFreeTrials() {
  try {
    const response = await axiosWithToken(functionBaseUrl + "/api/freeTrials", {
      method: "GET",
    });
    return response.data || [];
  } catch (error) {
    console.error("Error fetching free trials:", error);
    return [];
  }
}

/**
 * Fetches all users and categorizes them
 * Uses pagination to handle large collections and prevent timeouts
 */
export async function fetchAllUsers(deviceLimitExceptionList = []) {
  try {
    // Fetch all users in batches to prevent timeouts
    const usersDocs = await fetchCollectionInBatches("users", 500, 3);

    const allUsers = [];
    const allCoaches = [];
    const allStudents = [];
    const allPaidStudents = [];

    usersDocs.forEach(({ id, data }) => {
      let userData = data;
      if (!isTestAccount(userData?.userEmail, deviceLimitExceptionList)) {
        allUsers.push({ ...userData, id });
        if (
          userData?.subscriptionType?.length > 0 &&
          !userData?.subscriptionType?.includes("free")
        ) {
          if (userData.subscriptionType.includes("student_lite")) {
            allPaidStudents.push({ ...userData, id });
          } else {
            allCoaches.push({ ...userData, id });
          }
        } else {
          allStudents.push({ ...userData, id });
        }
      }
    });

    const result = {
      allUsers,
      allCoaches,
      allStudents,
      allPaidStudents,
    };
    return result;
  } catch (error) {
    console.error("Error fetching users:", error);
    return {
      allUsers: [],
      allCoaches: [],
      allStudents: [],
      allPaidStudents: [],
    };
  }
}

/**
 * Fetches staff students from API
 */
export async function fetchStaffStudents() {
  try {
    const response = await axiosWithToken(
      functionBaseUrl + "/api/staffStudents",
      {
        method: "POST",
      }
    );
    return response?.data || [];
  } catch (error) {
    console.error("Error fetching staff students:", error);
    return [];
  }
}
